import { load } from 'js-yaml'
import Path, { PathSettings } from '../models/Path.model'
import PathEvent, {
    Action,
    Availability,
    Appearance,
    CheckboxItem,
    Content,
    Dependency,
    EventRoles,
    Settings,
    AvailabilityAfterEvent
} from '../models/Event.model'
import Organization from '../models/settings/Organization.model'
import { CustomerCardFieldTypes } from '../models/settings/CustomerField'
import { PlanRole, PlanCustomerRequestedDetails } from '../models/Plan.model'
import { PlanTypeEnum } from './constants/plan-type'
import { ActionsEnum } from './constants/action-types'
import { EventTypesEnum } from './constants/event-types'
import { shortId } from './shortId'

const uuidv4 = require('uuid/v4')

// ─── Raw YAML types ───────────────────────────────────────────────────────────

interface YamlAction {
    label: string
    action_type?: string
    type?: string
}

interface YamlDependency {
    mode: 'time' | 'event'
    time_offset?: string
    after_event?: string
    dependent_action?: string
}

interface YamlEventRolesNested {
    executors?: string[]
    viewers?: string[]
    notified?: string[]
}

interface YamlEvent {
    title: string
    message?: string
    event_type?: string
    show_on_preview?: boolean
    executors?: string[]
    viewers?: string[]
    notified?: string[]
    roles?: YamlEventRolesNested
    actions?: YamlAction[]
    dependencies?: (YamlDependency | YamlDependency[])[]
    afterTime?: number
    afterEvents?: Array<{ eventTitle: string; endWith?: any }>
}

interface YamlPath {
    title: string
    show_on_preview?: boolean
    parallel_path?: string
    parallelWithPath?: string
    events?: YamlEvent[]
}

interface YamlRole {
    name: string
    access?: string
    isAdmin?: boolean
    isEditor?: boolean
    isExternal?: boolean
    uniqIdentifier?: string
}

interface YamlCustomerField {
    name: string
    required?: boolean
    isRequired?: boolean
    enabled?: boolean
    add_new?: boolean
    field_type?: string
}

interface RawYaml {
    title?: string
    plan_title?: string
    description?: string
    type?: string | number
    plan_type?: string
    paths?: YamlPath[]
    roles?: YamlRole[]
    customerFields?: YamlCustomerField[]
    customer_fields?: YamlCustomerField[]
}

// ─── Public result types ──────────────────────────────────────────────────────

export type ImportWarningType = 'unmapped_role' | 'unmapped_field' | 'broken_dependency' | 'parse_error'

export interface ImportWarning {
    type: ImportWarningType
    message: string
}

export interface FieldMention {
    name: string
    addNew: boolean                // from add_new in YAML
    fieldType: CustomerCardFieldTypes  // mapped from field_type
}

export interface YamlMentions {
    roleNames: string[]
    fieldNames: string[]       // convenience list of names
    fieldMentions: FieldMention[]
    parseError?: string
}

export interface YamlImportResult {
    title: string
    description: string
    planType?: PlanTypeEnum
    paths: Path[]
    roles: PlanRole[]
    customerDetails: PlanCustomerRequestedDetails[]
    warnings: ImportWarning[]
}

// ─── extractYamlMentions ──────────────────────────────────────────────────────

export function extractYamlMentions(yamlText: string): YamlMentions {
    let raw: RawYaml
    try {
        raw = (load(yamlText) as RawYaml) || {}
    } catch (err: any) {
        return { roleNames: [], fieldNames: [], fieldMentions: [], parseError: err.message }
    }

    const roleSet = new Set<string>()
    const fieldMap = new Map<string, FieldMention>()

    for (const r of raw.roles || []) {
        if (r.name) roleSet.add(r.name)
    }

    for (const path of raw.paths || []) {
        for (const event of path.events || []) {
            for (const n of [
                ...(event.executors || []), ...(event.viewers || []), ...(event.notified || []),
                ...(event.roles?.executors || []), ...(event.roles?.viewers || []), ...(event.roles?.notified || [])
            ]) roleSet.add(n)
        }
    }

    for (const f of [...(raw.customerFields || []), ...(raw.customer_fields || [])]) {
        if (!f.name || fieldMap.has(f.name)) continue
        fieldMap.set(f.name, {
            name: f.name,
            addNew: f.add_new ?? false,
            fieldType: parseFieldType(f.field_type || '')
        })
    }

    const fieldMentions = Array.from(fieldMap.values())
    return {
        roleNames: Array.from(roleSet),
        fieldNames: fieldMentions.map((f) => f.name),
        fieldMentions
    }
}

// ─── parseYamlPlan ────────────────────────────────────────────────────────────

export function parseYamlPlan(
    yamlText: string,
    organization?: Organization,
    roleResolutions?: Map<string, string>,
    fieldResolutions?: Map<string, string>
): YamlImportResult {
    const warnings: ImportWarning[] = []

    let raw: RawYaml
    try {
        raw = (load(yamlText) as RawYaml) || {}
    } catch (err: any) {
        return { title: '', description: '', paths: [], roles: [], customerDetails: [], warnings: [{ type: 'parse_error', message: `YAML parse error: ${err.message}` }] }
    }

    const yamlPaths = raw.paths || []

    // ── Pass 1: generate all IDs upfront ──────────────────────────────────────
    const pathIdMap    = new Map<string, string>()          // pathTitle → pathId
    const eventIdMap   = new Map<string, string>()          // eventTitle → eventId
    const eventTypeMap = new Map<string, EventTypesEnum>()  // eventTitle → type
    // For WithActions events: actionLabel → actionId
    const actionIdMap  = new Map<string, Map<string, string>>()
    // For Checkbox events: checkboxLabel → checkboxId
    const checkboxIdMap = new Map<string, Map<string, string>>()

    for (const yPath of yamlPaths) {
        const pk = yPath.title.toLowerCase()
        if (!pathIdMap.has(pk)) pathIdMap.set(pk, uuidv4())

        for (const yEvent of yPath.events || []) {
            const ek = yEvent.title.toLowerCase()
            if (!eventIdMap.has(ek)) eventIdMap.set(ek, uuidv4())

            const evType = parseEventType(yEvent.event_type || '')
            eventTypeMap.set(ek, evType)

            if (evType === EventTypesEnum.Checkbox) {
                // For checkbox events: YAML actions[] are the checkbox items
                const cbMap = new Map<string, string>()
                for (const a of yEvent.actions || []) cbMap.set(a.label.toLowerCase(), shortId())
                checkboxIdMap.set(ek, cbMap)
                actionIdMap.set(ek, new Map()) // no user-defined actions
            } else {
                const aMap = new Map<string, string>()
                for (const a of yEvent.actions || []) aMap.set(a.label.toLowerCase(), shortId())
                actionIdMap.set(ek, aMap)
            }
        }
    }

    // ── Pass 2 + 3: build objects + resolve dependencies ─────────────────────
    const paths: Path[] = []

    for (const yPath of yamlPaths) {
        const pathId = pathIdMap.get(yPath.title.toLowerCase())!

        const parallelTitle = yPath.parallel_path || yPath.parallelWithPath
        let parallelWithPath: string | undefined
        if (parallelTitle && parallelTitle.toLowerCase() !== 'none') {
            const pid = pathIdMap.get(parallelTitle.toLowerCase())
            if (pid) {
                parallelWithPath = pid
            } else {
                warnings.push({ type: 'broken_dependency', message: `Path "${yPath.title}": parallelWithPath "${parallelTitle}" not found.` })
            }
        }

        const pathSettings = new PathSettings(yPath.show_on_preview !== false, parallelWithPath)
        const events: PathEvent[] = []

        for (const yEvent of yPath.events || []) {
            const ek = yEvent.title.toLowerCase()
            const eventId = eventIdMap.get(ek)!
            const evType = eventTypeMap.get(ek)!
            const evActionMap = actionIdMap.get(ek)!
            const evCheckboxMap = checkboxIdMap.get(ek)

            // ── Actions / Checkboxes ──────────────────────────────────────────
            let actions: Action[] | undefined
            let checkboxes: CheckboxItem[] | undefined

            if (evType === EventTypesEnum.Checkbox) {
                // YAML actions[] → CheckboxItem[] for checkbox events
                checkboxes = (yEvent.actions || []).map((a) => {
                    const cbId = evCheckboxMap?.get(a.label.toLowerCase()) || shortId()
                    return new CheckboxItem(cbId, a.label, false)
                })
                // Provide the two default actions (End + Remind) that CheckboxesComponent expects
                actions = [
                    new Action(shortId(), 'Submit', ActionsEnum.End),
                    new Action(shortId(), 'Remind', ActionsEnum.Remind)
                ]
            } else {
                actions = (yEvent.actions || []).map((a) => {
                    const aId = evActionMap.get(a.label.toLowerCase()) || shortId()
                    return new Action(aId, a.label, parseActionType(a.action_type || a.type || ''))
                })
            }

            // ── Content ───────────────────────────────────────────────────────
            const content = new Content(evType, yEvent.message || '', actions, undefined, undefined, checkboxes)

            // ── Settings ──────────────────────────────────────────────────────
            const settings = new Settings(yEvent.show_on_preview !== false, false)

            // ── EventRoles ────────────────────────────────────────────────────
            const rawExec = yEvent.executors || yEvent.roles?.executors || []
            const rawView = yEvent.viewers   || yEvent.roles?.viewers   || []
            const rawNot  = yEvent.notified  || yEvent.roles?.notified  || []

            const eventRoles = (rawExec.length || rawView.length || rawNot.length)
                ? new EventRoles(
                    resolveRoleNames(rawExec, organization, roleResolutions, warnings, yEvent.title, 'executors'),
                    resolveRoleNames(rawView, organization, roleResolutions, warnings, yEvent.title, 'viewers'),
                    resolveRoleNames(rawNot,  organization, roleResolutions, warnings, yEvent.title, 'notified')
                )
                : undefined

            // ── Dependencies ──────────────────────────────────────────────────
            // Outer array = OR groups, each inner array = AND group (all must be met).
            // Each OR group becomes its own Dependency object; the runtime evaluates
            // multiple Dependency objects with OR logic.
            const dependencies: Dependency[] = []

            const parseDependencyGroup = (group: YamlDependency[]): Dependency => {
                let afterTime = 0
                const afterEvents: AvailabilityAfterEvent[] = []

                for (const dep of group) {
                    if (dep.mode === 'time') {
                        const t = parseTimeOffset(dep.time_offset || '')
                        if (t > afterTime) afterTime = t
                    } else if (dep.mode === 'event' && dep.after_event) {
                        const refKey = dep.after_event.toLowerCase()
                        const refId = eventIdMap.get(refKey)
                        if (!refId) {
                            warnings.push({ type: 'broken_dependency', message: `Event "${yEvent.title}": after_event "${dep.after_event}" not found.` })
                            continue
                        }

                        const refType = eventTypeMap.get(refKey)
                        let endWith: AvailabilityAfterEvent['endWith'] = { type: 'any', value: undefined }

                        if (dep.dependent_action) {
                            if (refType === EventTypesEnum.Checkbox) {
                                const refCbMap = checkboxIdMap.get(refKey)
                                const cbId = refCbMap?.get(dep.dependent_action.toLowerCase())
                                if (cbId) {
                                    endWith = { type: 'checkboxes', value: [cbId] }
                                } else {
                                    warnings.push({ type: 'broken_dependency', message: `Event "${yEvent.title}": checkbox "${dep.dependent_action}" not found on "${dep.after_event}".` })
                                }
                            } else {
                                const refAMap = actionIdMap.get(refKey)
                                const aId = refAMap?.get(dep.dependent_action.toLowerCase())
                                if (aId) {
                                    endWith = { type: 'action', value: aId }
                                } else {
                                    warnings.push({ type: 'broken_dependency', message: `Event "${yEvent.title}": action "${dep.dependent_action}" not found on "${dep.after_event}".` })
                                }
                            }
                        }

                        afterEvents.push({ eventId: refId, endWith })
                    }
                }

                return new Dependency(new Availability(afterTime, afterEvents), new Appearance(0))
            }

            if (yEvent.dependencies && yEvent.dependencies.length > 0) {
                // Normalize: a plain object = AND group of 1; an array = explicit AND group
                const groups: YamlDependency[][] = yEvent.dependencies.map((dep) => (Array.isArray(dep) ? dep : [dep]))
                for (const group of groups) {
                    dependencies.push(parseDependencyGroup(group))
                }
            } else if (yEvent.afterEvents) {
                // old schema
                const afterTime = yEvent.afterTime || 0
                const afterEvents: AvailabilityAfterEvent[] = []
                for (const ae of yEvent.afterEvents) {
                    const refId = eventIdMap.get(ae.eventTitle.toLowerCase())
                    if (refId) {
                        afterEvents.push({ eventId: refId, endWith: ae.endWith || { type: 'any', value: undefined } })
                    } else {
                        warnings.push({ type: 'broken_dependency', message: `Event "${yEvent.title}": afterEvent "${ae.eventTitle}" not found.` })
                    }
                }
                dependencies.push(new Dependency(new Availability(afterTime, afterEvents), new Appearance(0)))
            }

            // No dependencies specified → initial event (no prerequisites)
            if (dependencies.length === 0) {
                dependencies.push(new Dependency(new Availability(0, []), new Appearance(0)))
            }

            events.push(new PathEvent(yEvent.title, eventId, dependencies as [Dependency], content, settings, undefined, eventRoles))
        }

        const path = new Path(yPath.title, pathId, undefined, [events[0] || new PathEvent('')] as [PathEvent], pathSettings)
        path.events = (events.length > 0 ? events : [new PathEvent('')]) as [PathEvent]
        paths.push(path)
    }

    // ── Plan-level roles ──────────────────────────────────────────────────────
    const roles: PlanRole[] = []
    for (const yr of raw.roles || []) {
        const { isAdmin, isEditor, isExternal } = parseRoleAccess(yr)
        const roleId = resolveRoleId(yr.name, organization, roleResolutions, warnings)
        roles.push({ roleId: roleId ?? yr.name, isAdmin, isEditor, isExternal, uniqIdentifier: yr.uniqIdentifier })
    }

    // ── Customer fields ───────────────────────────────────────────────────────
    const customerDetails: PlanCustomerRequestedDetails[] = []
    const allFields = [...(raw.customerFields || []), ...(raw.customer_fields || [])]
    const seenFieldNames = new Set<string>()

    for (const yf of allFields) {
        if (seenFieldNames.has(yf.name)) continue
        seenFieldNames.add(yf.name)

        const isRequired = yf.required ?? yf.isRequired ?? false
        let fieldId: string | null = null

        if (fieldResolutions?.has(yf.name)) {
            fieldId = fieldResolutions.get(yf.name)!
        } else if (organization) {
            const match = organization.customersFields.find((f) => f.name.toLowerCase() === yf.name.toLowerCase() && !f.isDeleted)
            fieldId = match?.id ?? null
        }

        if (fieldId) {
            customerDetails.push({ fieldId, isRequired })
        } else {
            warnings.push({ type: 'unmapped_field', message: `Customer field "${yf.name}" not resolved — skipped.` })
        }
    }

    return { title: raw.plan_title || raw.title || '', description: raw.description || '', planType: parsePlanType(raw.plan_type ?? raw.type), paths, roles, customerDetails, warnings }
}

// ─── Enum / type parsers ──────────────────────────────────────────────────────

function parsePlanType(raw?: string | number): PlanTypeEnum | undefined {
    if (raw === undefined || raw === null) return undefined
    const s = String(raw).toLowerCase().trim()
    if (s === 'multi users' || s === 'multiusers') return PlanTypeEnum.MultiUsers
    if (s === 'single')                             return PlanTypeEnum.Single
    if (s === 'multi' || s === 'multi customers')   return PlanTypeEnum.Multi
    if (s === 'template')                           return PlanTypeEnum.Template
    if (s === 'sharing')                            return PlanTypeEnum.Sharing
    if (s === 'sales')                              return PlanTypeEnum.Sales
    if (!isNaN(Number(raw)))                        return Number(raw) as PlanTypeEnum
    return undefined
}

function parseEventType(raw: string): EventTypesEnum {
    const s = raw.toLowerCase().trim()
    if (s === 'checkboxes' || s === 'checkbox') return EventTypesEnum.Checkbox
    if (s === 'tip')  return EventTypesEnum.Tip
    if (s === 'form') return EventTypesEnum.Form
    return EventTypesEnum.WithActions
}

function parseActionType(raw: string): ActionsEnum {
    const s = raw.toLowerCase().trim()
    if (s === 'end')                                    return ActionsEnum.End
    if (s === 'remind')                                 return ActionsEnum.Remind
    if (s === 'add picture' || s === 'takepicture')     return ActionsEnum.TakePicture
    if (s === 'add file' || s === 'finishandaddthefiles') return ActionsEnum.FinishAndAddTheFiles
    if (s === 'link and finish' || s === 'linkandfinish') return ActionsEnum.LinkAndFinish
    if (s === 'link')   return ActionsEnum.Link
    if (s === 'call')   return ActionsEnum.Call
    if (s === 'repeat') return ActionsEnum.Repeat
    return ActionsEnum.End
}

function parseRoleAccess(yr: YamlRole): { isAdmin: boolean; isEditor: boolean; isExternal: boolean } {
    if (yr.access) {
        const a = yr.access.toLowerCase()
        return { isAdmin: a === 'admin', isEditor: a === 'editor', isExternal: a === 'external' }
    }
    return { isAdmin: yr.isAdmin || false, isEditor: yr.isEditor || false, isExternal: yr.isExternal || false }
}

function parseTimeOffset(raw: string): number {
    const m = raw.match(/(\d+(\.\d+)?)/)
    return m ? parseFloat(m[1]) : 0
}

export function parseFieldType(raw: string): CustomerCardFieldTypes {
    const s = (raw || '').toLowerCase().trim()
    if (s === 'phone')       return 'phone'
    if (s === 'email')       return 'email'
    if (s === 'number')      return 'number'
    if (s === 'list')        return 'textList'
    if (s === 'text/number') return 'number'
    return 'text'
}

// ─── Role resolution helpers ──────────────────────────────────────────────────

function resolveRoleId(
    name: string,
    organization: Organization | undefined,
    resolutions: Map<string, string> | undefined,
    warnings: ImportWarning[]
): string | null {
    if (resolutions?.has(name)) return resolutions.get(name)!
    if (organization) {
        const match = organization.roles.find((r) => r.text.toLowerCase() === name.toLowerCase())
        if (match) return match.id
        warnings.push({ type: 'unmapped_role', message: `Role "${name}" not found in organization — stored as-is.` })
    } else {
        warnings.push({ type: 'unmapped_role', message: `Role "${name}": no organization loaded.` })
    }
    return null
}

function resolveRoleNames(
    names: string[],
    organization: Organization | undefined,
    resolutions: Map<string, string> | undefined,
    warnings: ImportWarning[],
    eventTitle: string,
    field: string
): string[] {
    return names.map((name) => {
        if (resolutions?.has(name)) return resolutions.get(name)!
        if (organization) {
            const match = organization.roles.find((r) => r.text.toLowerCase() === name.toLowerCase())
            if (match) return match.id
            warnings.push({ type: 'unmapped_role', message: `Event "${eventTitle}" ${field}: role "${name}" not found.` })
        }
        return name
    })
}
